import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { selectUser, updateUser } from "@/features/auth/authSlice";
import { usePermission } from "@/hooks/usePermission";
import { API_ENDPOINTS } from "@/lib/api/config";
import apiClient, { setAuthToken } from "@/lib/api/apiClient";
import { STORAGE_KEYS } from "@/constants/storageKeys";
import {
  getApplicationDetail,
  getResidentApplicationAddresses,
  getResidentApplicationEmploymentContext,
  impersonateUser,
  updateResidentApplication,
  getPolicyDeclarationFiles,
  type PolicyDeclarationFileRow,
} from "../services/applicationService";
import { showSuccess, showError, showInfo, showValidationMessage } from "@/lib/utils/notifications";
import { sendAnalytics } from "@/lib/utils/analytics";
import { getCurrentPathForImpersonateReturn } from "@/lib/utils/impersonateReturnPath";
import { openServeFileInNewTab } from "@/lib/utils/serveFileHelpers";
import { isSharedPhoneError } from "../utils/aboutYourselfValidator";
import { validateEditableTabBeforeSwitch } from "../utils/applicationDetailTabSwitchValidation";
import { useNinjaEquifaxPanelPosition } from "./useNinjaEquifaxPanelPosition";
import { NINJA_EQUIFAX_SCORE_UPDATE_ROLES, isNinjaEquifaxScoreUpdateHost } from "../utils/applicationConstants";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../types/application";
import type {
  PaymentAdjustmentHistoryLine,
} from "../types/modals";
import type {
  ApplicationDetailFormDelta,
  LeaseChangeAction,
  TabType,
} from "../types/pages";
import type { UpdateResidentApplicationPayload, ResidentApplicationEmploymentContext } from "../types/services";
import type { PaymentHistoryItem } from "@/types/application";
import {
  findFirstContractPayment,
  getUnknownErrorMessage,
  hasContractDataForRisk,
  isApiOne,
  isBeforeToday,
  isPresent,
  mergeAddressRaIds,
  mergeEmploymentResidentContext,
  normalizeApplicantId,
  resolveApplicationLayerFromDetail,
} from "../pages/applicationDetailHelpers";
import { buildApplicationTabSavePayload } from "../pages/applicationDetailSavePayload";
import {
  shouldOfferEmploymentImport,
  buildEmploymentImportUpdate,
} from "../utils/employmentImport";
import type { EmploymentImportMode } from "../types/utils";
import { shouldOfferAddressImport, buildAddressImportUpdate, ADDRESS_IMPORT_SUCCESS_MESSAGE } from "../utils/addressImport";
import type { ResidentApplicationData } from "@/types/application";

export function useApplicationDetail() {
  const { applicantId: applicantIdParam } = useParams<{ applicantId: string }>();
  // Angular parity: strip leading zeros from route applicant id.
  const applicantId = applicantIdParam
    ? normalizeApplicantId(applicantIdParam)
    : applicantIdParam;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { hasPolicy } = usePermission();
  const showUpdateNinjaEquifaxScoresButton =
    isNinjaEquifaxScoreUpdateHost() &&
    NINJA_EQUIFAX_SCORE_UPDATE_ROLES.includes(
      user?.current_role?.role_name ?? "",
    ) && hasPolicy(["application_detail_view"]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applicationAllData, setApplicationAllData] =
    useState<ApplicationDetailData | null>(null);
  const [applicationData, setApplicationData] =
    useState<ApplicationLayerData | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabType>("aboutyourselfinfo");
  const [showChecklist, setShowChecklist] = useState(false);
  const [isCoresident, setIsCoresident] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [formData, setFormData] = useState<ApplicationDetailFormDelta>({});
  const [dirtyTabs, setDirtyTabs] = useState<Partial<Record<TabType, boolean>>>({});
  const [aboutYourselfExposeInlineValidation, setAboutYourselfExposeInlineValidation] =
    useState(false);
  const [propertyExposeInlineValidation, setPropertyExposeInlineValidation] =
    useState(false);
  const [incomeExposeInlineValidation, setIncomeExposeInlineValidation] =
    useState(false);
  const pendingTabAfterSaveRef = useRef<TabType | null>(null);
  const saveModeRef = useRef<"explicitUpdate" | "tabChangeSave">("explicitUpdate");

  const [tabChangeSaveConfirm, setTabChangeSaveConfirm] = useState<{
    open: boolean;
    targetTab: TabType | null;
  }>({ open: false, targetTab: null });

  const [tabChangeInvalidConfirm, setTabChangeInvalidConfirm] = useState<{
    open: boolean;
    targetTab: TabType | null;
    message: string;
  }>({ open: false, targetTab: null, message: "" });

  const [rentAdjustmentModal, setRentAdjustmentModal] = useState<{
    show: boolean;
    payment: PaymentAdjustmentHistoryLine | null;
    autoSelectedData: { adjusttype: string; adjustsubtype?: string } | null;
    afterRiskRating?: boolean;
  }>({ show: false, payment: null, autoSelectedData: null });
  const [paymentProcessModalOpen, setPaymentProcessModalOpen] = useState(false);

  const [showRiskRatingModal, setShowRiskRatingModal] = useState(false);
  const [showMaxRentModal, setShowMaxRentModal] = useState(false);
  const [maxRentHistoryModalKey, setMaxRentHistoryModalKey] = useState(0);
  const [policyDeclarationModalOpen, setPolicyDeclarationModalOpen] = useState(false);
  const [policyDeclarationFiles, setPolicyDeclarationFiles] = useState<
    PolicyDeclarationFileRow[]
  >([]);
  const [policyDeclarationAccessLoading, setPolicyDeclarationAccessLoading] =
    useState(false);
  const [scoresInputsHidden, setScoresInputsHidden] = useState(true);
  const [reimbursementExplanationOpen, setReimbursementExplanationOpen] =
    useState(false);
  const [validationModal, setValidationModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    showValidationIcon?: boolean;
  }>({ open: false, title: "", message: "" });
  const [sharedPhoneErrorOpen, setSharedPhoneErrorOpen] = useState(false);
  const [addressErrorOpen, setAddressErrorOpen] = useState(false);
  const [importPreviousAddressOpen, setImportPreviousAddressOpen] =
    useState(false);
  const [importPreviousDismissed, setImportPreviousDismissed] = useState(false);
  const [importPreviousEmploymentOpen, setImportPreviousEmploymentOpen] =
    useState(false);
  const [importPreviousEmploymentDismissed, setImportPreviousEmploymentDismissed] =
    useState(false);
  const [leaseDetailsChangedConfirmOpen, setLeaseDetailsChangedConfirmOpen] =
    useState(false);
  const [leaseStartDateAdjustConfirmOpen, setLeaseStartDateAdjustConfirmOpen] =
    useState(false);
  const [reimbursementApplicationDetailsConfirmOpen, setReimbursementApplicationDetailsConfirmOpen] =
    useState(false);
  const [noEmploymentModalOpen, setNoEmploymentModalOpen] = useState(false);
  const noEmploymentConfirmedRef = useRef(false);
  const leaseChangeActionRef = useRef<LeaseChangeAction | null>(null);
  const submitWithoutLeaseDateUpdateRef = useRef(false);
  const [updateTriggerCounter, setUpdateTriggerCounter] = useState(0);
  const updateButtonRef = useRef<HTMLButtonElement | null>(null);

  const {
    ninjaScoreTogglePrimaryRef,
    ninjaScoreToggleCoRef,
    ninjaPanelPrimaryRef,
    ninjaPanelCoRef,
  } = useNinjaEquifaxPanelPosition({
    applicationAllData,
    applicationData,
    scoresInputsHidden,
    showUpdateNinjaEquifaxScoresButton,
  });

  const refreshApplicationDetail = async () => {
    if (!applicantId) return undefined;
    // Enrich addresses with ra_id from resident_application so saves UPDATE, not INSERT.
    const [data, residentAddresses] = await Promise.all([
      getApplicationDetail(applicantId),
      getResidentApplicationAddresses(applicantId).catch(() => null),
    ]);
    setApplicationAllData(data);
    const layer = resolveApplicationLayerFromDetail(data, applicantId);
    setApplicationData(mergeAddressRaIds(layer.applicationData, residentAddresses));
    setIsCoresident(layer.isCoresident);
    return data;
  };

  useEffect(() => {
    if (updateTriggerCounter > 0) {
      updateButtonRef.current?.click();
    }
  }, [updateTriggerCounter]);

  useEffect(() => {
    const fragment = (location.hash || "").replace(/^#/, "");
    if (fragment === "notification_notes") {
      setSelectedTab("notesinfo");
    } else if (fragment === "notification_credit") {
      setSelectedTab("creditinfo");
    } else if (fragment === "payment") {
      setSelectedTab("signatureinfo");
      navigate(location.pathname, { replace: true });
    } else if (fragment === "default_payment") {
      setSelectedTab("signatureinfo");
      sessionStorage.setItem("pending_default_payment", "1");
      navigate(location.pathname, { replace: true });
    }
  }, [location.hash, location.pathname, navigate]);

  useEffect(() => {
    setIsEdit(false);
    setFormData({});
    setDirtyTabs({});
    setAboutYourselfExposeInlineValidation(false);
    setPropertyExposeInlineValidation(false);
    setIncomeExposeInlineValidation(false);
    setLeaseDetailsChangedConfirmOpen(false);
    setLeaseStartDateAdjustConfirmOpen(false);
    setReimbursementApplicationDetailsConfirmOpen(false);
    leaseChangeActionRef.current = null;
    submitWithoutLeaseDateUpdateRef.current = false;
    noEmploymentConfirmedRef.current = false;
    setImportPreviousDismissed(false);
    setImportPreviousEmploymentDismissed(false);
  }, [applicantId]);

  useEffect(() => {
    if (
      isEdit &&
      selectedTab === "currentaddressinfo" &&
      !importPreviousDismissed &&
      shouldOfferAddressImport(applicationData as ResidentApplicationData | null | undefined)
    ) {
      setImportPreviousAddressOpen(true);
    }
  }, [isEdit, selectedTab, applicationData, importPreviousDismissed]);

  // Angular resident-application-add: load oldapplication for About Yourself import prompt.
  useEffect(() => {
    if (!applicantId || !isEdit || selectedTab !== "currentaddressinfo") return;

    let cancelled = false;
    void getResidentApplicationAddresses(applicantId)
      .then((residentData) => {
        if (cancelled) return;
        setApplicationData((prev) => {
          if (!prev) return prev;
          return mergeAddressRaIds(prev, residentData);
        });
      })
      .catch(() => {
        // Non-blocking.
      });

    return () => {
      cancelled = true;
    };
  }, [applicantId, isEdit, selectedTab]);

  // Angular resident-application-add: load employment context for import prompt.
  useEffect(() => {
    if (!applicantId || !isEdit || selectedTab !== "employmentinfo") return;

    let cancelled = false;
    void getResidentApplicationEmploymentContext(applicantId)
      .then((employmentContext: ResidentApplicationEmploymentContext) => {
        if (cancelled) return;
        setApplicationData((prev) => {
          if (!prev) return prev;
          return mergeEmploymentResidentContext(prev, employmentContext);
        });
      })
      .catch(() => {
        // Non-blocking.
      });

    return () => {
      cancelled = true;
    };
  }, [applicantId, isEdit, selectedTab]);

  useEffect(() => {
    if (
      isEdit &&
      selectedTab === "employmentinfo" &&
      !importPreviousEmploymentDismissed &&
      shouldOfferEmploymentImport(
        applicationData as ResidentApplicationData | null | undefined,
        !isEdit,
      )
    ) {
      setImportPreviousEmploymentOpen(true);
    }
  }, [
    isEdit,
    selectedTab,
    applicationData,
    importPreviousEmploymentDismissed,
  ]);

  useEffect(() => {
    if (!applicantId) {
      setError("Invalid applicant ID");
      setLoading(false);
      return;
    }

    const fetchDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const [data, residentAddresses] = await Promise.all([
          getApplicationDetail(applicantId),
          getResidentApplicationAddresses(applicantId).catch(() => null),
        ]);
        setApplicationAllData(data);
        const layer = resolveApplicationLayerFromDetail(data, applicantId);
        setApplicationData(mergeAddressRaIds(layer.applicationData, residentAddresses));
        setIsCoresident(layer.isCoresident);
      } catch (err: unknown) {
        setError(getUnknownErrorMessage(err, "Failed to load application details"));
      } finally {
        setLoading(false);
      }
    };

    void fetchDetail();
  }, [applicantId]);

  useEffect(() => {
    if (!isEdit) {
      setAboutYourselfExposeInlineValidation(false);
      setPropertyExposeInlineValidation(false);
      setIncomeExposeInlineValidation(false);
    }
  }, [isEdit]);

  useEffect(() => {
    if (selectedTab !== "propertyinfo") {
      setPropertyExposeInlineValidation(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== "employmentinfo") {
      setIncomeExposeInlineValidation(false);
    }
  }, [selectedTab]);

  const handleTabChange = (tab: TabType) => {
    const editableTabs: TabType[] = [
      "aboutyourselfinfo",
      "currentaddressinfo",
      "propertyinfo",
      "employmentinfo",
    ];

    if (!isEdit || !editableTabs.includes(tab) || !editableTabs.includes(selectedTab)) {
      setSelectedTab(tab);
      return;
    }

    if (tab === selectedTab) return;

    const vr = validateEditableTabBeforeSwitch(
      selectedTab as
        | "aboutyourselfinfo"
        | "currentaddressinfo"
        | "propertyinfo"
        | "employmentinfo",
      formData,
      applicationData,
      applicationAllData,
    );
    if (!vr.ok) {
      if (vr.kind === "about_address") {
        setAboutYourselfExposeInlineValidation(true);
        setAddressErrorOpen(true);
        return;
      }
      setTabChangeInvalidConfirm({
        open: true,
        targetTab: tab,
        message: vr.errors.join("\n"),
      });
      return;
    }

    if (dirtyTabs[selectedTab]) {
      setTabChangeSaveConfirm({ open: true, targetTab: tab });
      return;
    }

    setSelectedTab(tab);
  };

  const handleCoResidentClick = (coresidentId: string) => {
    sendAnalytics('click', 'resident switch', '');
    navigate(`/application/${coresidentId}`);
  };

  const performImpersonate = async () => {
    if (!applicationData?.role_id) return;

    try {
      const currentToken = localStorage.getItem(STORAGE_KEYS.authToken);
      if (currentToken) {
        localStorage.setItem("admin_token", currentToken);
      }

      const returnPath = getCurrentPathForImpersonateReturn();
      localStorage.setItem("impersonate_path", returnPath);
      localStorage.setItem("impersonatepath", returnPath);

      if (applicantId) {
        localStorage.setItem("impersonated_applicant_id", applicantId);
      }

      sessionStorage.setItem("impersonate_process", "1");

      const token = await impersonateUser(applicationData.role_id);
      setAuthToken(token);
      localStorage.setItem(STORAGE_KEYS.authToken, token);

      const verifyResponse = await apiClient.get(API_ENDPOINTS.VERIFY_USER + "?force");
      if (verifyResponse.data) {
        dispatch(updateUser(verifyResponse.data));
        localStorage.setItem("impersonate", "true");
      }

      window.location.href = `/resident-application/${applicantId}`;
    } catch (err: unknown) {
      console.error("Failed to impersonate:", err);
      setError(getUnknownErrorMessage(err, "Failed to impersonate user"));
    }
  };

  const handleNinjaEquifaxScoreToolbarClick = () => {
    setScoresInputsHidden((hidden) => !hidden);
  };

  const handleRiskRatingAdjustClick = () => {
    if (!applicationAllData) return;
    if (hasContractDataForRisk(applicationAllData)) {
      const payment = findFirstContractPayment(applicationAllData);
      if (payment) {
        setRentAdjustmentModal({
          show: true,
          payment,
          autoSelectedData: null,
          afterRiskRating: true,
        });
        return;
      }
    }
    setShowRiskRatingModal(true);
  };

  const handleDownloadTerms = async (
    e: React.MouseEvent,
    options?: { trackAccessConfirmationLoading?: boolean },
  ) => {
    e.preventDefault();

    const certPath =
      applicationAllData?.certificate_path || applicationData?.certificate_path;
    const applicationId = applicationAllData?.application_id;

    if (!applicationId) {
      showError("Application not found.");
      return;
    }

    if (options?.trackAccessConfirmationLoading) {
      setPolicyDeclarationAccessLoading(true);
    }
    try {
      const files = await getPolicyDeclarationFiles(applicationId);
      if (files.length === 0) {
        showValidationMessage("No files found");
        if (certPath && (certPath.startsWith("http") || certPath.startsWith("/"))) {
          window.open(certPath, "_blank", "noopener,noreferrer");
        }
        return;
      }
      if (files.length === 1) {
        const streamUrl = files[0]?.stream_url || "";
        if (!streamUrl) {
          showValidationMessage("File URL not available");
          return;
        }
        await openServeFileInNewTab(streamUrl);
        return;
      }
      setPolicyDeclarationFiles(files);
      setPolicyDeclarationModalOpen(true);
    } catch (err: unknown) {
      showError(
        err instanceof Error ? err.message : "Failed to load policy declaration files.",
      );
      if (certPath && (certPath.startsWith("http") || certPath.startsWith("/"))) {
        window.open(certPath, "_blank", "noopener,noreferrer");
      }
    } finally {
      if (options?.trackAccessConfirmationLoading) {
        setPolicyDeclarationAccessLoading(false);
      }
    }
  };

  const resetEditState = () => {
    setIsEdit(false);
    setFormData({});
    setDirtyTabs({});
    setLeaseDetailsChangedConfirmOpen(false);
    setLeaseStartDateAdjustConfirmOpen(false);
    setReimbursementApplicationDetailsConfirmOpen(false);
    leaseChangeActionRef.current = null;
    submitWithoutLeaseDateUpdateRef.current = false;
  };

  const handleFormChange = (data: ApplicationDetailFormDelta) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setDirtyTabs((prev) => ({ ...prev, [selectedTab]: true }));
  };

  const handleSave = async () => {
    if (!applicantId || !applicationData || !applicationAllData) return;

    try {
      if (saveModeRef.current !== "tabChangeSave") {
        saveModeRef.current = "explicitUpdate";
      }

      if (applicationData?.allow_edit === false) {
        showError(
          "This application is locked and cannot be updated. Contact support if you need changes to an approved application.",
        );
        return;
      }

      if (
        selectedTab === "propertyinfo" &&
        !submitWithoutLeaseDateUpdateRef.current
      ) {
        const effectiveLeaseStartDate = Object.prototype.hasOwnProperty.call(
          formData,
          "lease_start_date",
        )
          ? formData.lease_start_date
          : applicationAllData?.lease_start_date;
        const effectiveRentAmount = Object.prototype.hasOwnProperty.call(
          formData,
          "rent_amount",
        )
          ? formData.rent_amount
          : applicationAllData?.rent_amount;

        if (
          effectiveLeaseStartDate &&
          isBeforeToday(effectiveLeaseStartDate) &&
          isPresent(effectiveRentAmount) &&
          !(applicationData?.rent_added || applicationAllData?.rent_added) &&
          !isApiOne(
            applicationData?.dont_update_lease_date ??
              applicationAllData?.dont_update_lease_date,
          )
        ) {
          setLeaseStartDateAdjustConfirmOpen(true);
          return;
        }
      }

      const buildResult = buildApplicationTabSavePayload({
        selectedTab,
        formData,
        applicationData,
        applicationAllData,
        noEmploymentConfirmed: noEmploymentConfirmedRef.current,
        leaseChangeAction: leaseChangeActionRef.current,
        submitWithoutLeaseDateUpdate: submitWithoutLeaseDateUpdateRef.current,
        tabDirty: Boolean(dirtyTabs[selectedTab]),
      });

      if (!buildResult.ok) {
        switch (buildResult.kind) {
          case "address_error":
            setAboutYourselfExposeInlineValidation(true);
            setAddressErrorOpen(true);
            return;
          case "validation":
            if (
              buildResult.tab === "aboutyourselfinfo" ||
              buildResult.tab === "currentaddressinfo"
            ) {
              setAboutYourselfExposeInlineValidation(true);
            }
            if (buildResult.tab === "propertyinfo") {
              setPropertyExposeInlineValidation(true);
            }
            if (buildResult.tab === "employmentinfo") {
              setIncomeExposeInlineValidation(true);
              window.dispatchEvent(
                new CustomEvent("liberty:income-employment:validate"),
              );
            }
            setValidationModal({
              open: true,
              title: "Missing Fields",
              message: buildResult.errors.join("\n"),
            });
            return;
          case "no_employment":
            setNoEmploymentModalOpen(true);
            return;
          case "lease_start_adjust":
            setLeaseStartDateAdjustConfirmOpen(true);
            return;
          case "lease_details_changed":
            setLeaseDetailsChangedConfirmOpen(true);
            return;
          case "reimbursement_confirm":
            setReimbursementApplicationDetailsConfirmOpen(true);
            return;
          default:
            return;
        }
      }

      await updateResidentApplication(
        applicantId,
        buildResult.requestData as UpdateResidentApplicationPayload,
      );
      if (selectedTab === "aboutyourselfinfo") {
        const saved = buildResult.requestData;
        setApplicationData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            user_data: {
              ...((prev.user_data as Record<string, unknown> | undefined) ?? {}),
              first_name: saved.first_name as string | undefined,
              middle_name: saved.middle_name as string | undefined,
              last_name: saved.last_name as string | undefined,
              suffix: saved.suffix as string | undefined,
            },
          };
        });
      }
      leaseChangeActionRef.current = null;
      submitWithoutLeaseDateUpdateRef.current = false;
      noEmploymentConfirmedRef.current = false;
      setAboutYourselfExposeInlineValidation(false);
      setPropertyExposeInlineValidation(false);
      setIncomeExposeInlineValidation(false);
      showSuccess("Application updated successfully");

      const oldRentAmount =
        selectedTab === "propertyinfo"
          ? Number(applicationAllData?.rent_amount || 0)
          : null;
      const data = await refreshApplicationDetail();

      if (oldRentAmount !== null && data?.rent_amount) {
        const newRentAmount = Number(data.rent_amount);
        if (oldRentAmount !== newRentAmount) {
          const contractData = data.payment_history?.find(
            (payment: PaymentHistoryItem) =>
              ["CONTRACT", "CONTRACT_INVOICE"].includes(payment.payment_type ?? "") &&
              payment.transaction_status !== "Refunded",
          );
          if (contractData) {
            const balanceDue = Number(data.balance_due || 0);
            if (balanceDue > 0) {
              setRentAdjustmentModal({
                show: true,
                payment: contractData as PaymentAdjustmentHistoryLine,
                autoSelectedData: { adjusttype: "increase" },
              });
            } else if (balanceDue < 0) {
              setRentAdjustmentModal({
                show: true,
                payment: contractData as PaymentAdjustmentHistoryLine,
                autoSelectedData: { adjusttype: "decrease" },
              });
            }
          }
        }
      }

      if (saveModeRef.current === "tabChangeSave") {
        const nextTab = pendingTabAfterSaveRef.current;
        pendingTabAfterSaveRef.current = null;
        saveModeRef.current = "explicitUpdate";
        setFormData({});
        setDirtyTabs({});
        if (nextTab) setSelectedTab(nextTab);
      } else {
        resetEditState();
      }
    } catch (err: unknown) {
      leaseChangeActionRef.current = null;
      submitWithoutLeaseDateUpdateRef.current = false;
      console.error("Failed to update:", err);
      const message = getUnknownErrorMessage(err, "Failed to update application");
      if (
        selectedTab === "aboutyourselfinfo" &&
        isSharedPhoneError(message) &&
        formData?.shared_phone_no !== true
      ) {
        setSharedPhoneErrorOpen(true);
      } else {
        setValidationModal({
          open: true,
          title: "Validation Message",
          message,
          showValidationIcon: false,
        });
      }
    }
  };

  const handleCancelEdit = () => {
    resetEditState();
    void refreshApplicationDetail().catch((err: unknown) => {
      console.error("Failed to reload:", err);
    });
  };

  const importPreviousAddresses = () => {
    if (!applicationData) return;
    const next = buildAddressImportUpdate(
      applicationData as ResidentApplicationData,
    );
    if (!next) return;
    setApplicationData(next as ApplicationLayerData);
    setFormData((prev) => {
      const cleared = { ...prev };
      delete cleared.previous_address;
      if (cleared.deleted && typeof cleared.deleted === "object") {
        const deleted = { ...(cleared.deleted as Record<string, unknown>) };
        delete deleted.previous_address;
        cleared.deleted =
          Object.keys(deleted).length > 0 ? deleted : undefined;
      }
      return cleared;
    });
    setDirtyTabs((prev) => ({ ...prev, currentaddressinfo: true }));
    setImportPreviousAddressOpen(false);
    setImportPreviousDismissed(true);
    showInfo(ADDRESS_IMPORT_SUCCESS_MESSAGE);
  };

  const importPreviousEmployment = (mode: EmploymentImportMode) => {
    if (!applicationData) return;
    const next = buildEmploymentImportUpdate(
      applicationData as ResidentApplicationData,
      mode,
    );
    if (!next) return;
    setApplicationData(next as ApplicationLayerData);
    setFormData((prev) => {
      const next = { ...prev };
      delete next.employment_status;
      delete next.current_employment;
      delete next.previous_employment;
      delete next.other_source;
      delete next.deleted;
      delete next.enter_date;
      delete next.ninja_reason;
      return next;
    });
    setDirtyTabs((prev) => ({ ...prev, employmentinfo: true }));
    setImportPreviousEmploymentOpen(false);
    setImportPreviousEmploymentDismissed(true);
    showInfo("Added Employment data");
  };

  const dismissImportPreviousEmployment = () => {
    setImportPreviousEmploymentOpen(false);
    setImportPreviousEmploymentDismissed(true);
  };

  return {
    applicantId,
    user,
    hasPolicy,
    loading,
    error,
    applicationAllData,
    applicationData,
    selectedTab,
    showChecklist,
    isCoresident,
    isEdit,
    formData,
    aboutYourselfExposeInlineValidation,
    propertyExposeInlineValidation,
    incomeExposeInlineValidation,
    showImpersonateModal,
    tabChangeSaveConfirm,
    tabChangeInvalidConfirm,
    rentAdjustmentModal,
    paymentProcessModalOpen,
    showRiskRatingModal,
    showMaxRentModal,
    maxRentHistoryModalKey,
    policyDeclarationModalOpen,
    policyDeclarationFiles,
    policyDeclarationAccessLoading,
    scoresInputsHidden,
    reimbursementExplanationOpen,
    validationModal,
    sharedPhoneErrorOpen,
    addressErrorOpen,
    importPreviousAddressOpen,
    importPreviousEmploymentOpen,
    leaseDetailsChangedConfirmOpen,
    leaseStartDateAdjustConfirmOpen,
    reimbursementApplicationDetailsConfirmOpen,
    noEmploymentModalOpen,
    updateButtonRef,
    showUpdateNinjaEquifaxScoresButton,
    ninjaScoreTogglePrimaryRef,
    ninjaScoreToggleCoRef,
    ninjaPanelPrimaryRef,
    ninjaPanelCoRef,
    setShowChecklist,
    setSelectedTab,
    setScoresInputsHidden,
    setShowImpersonateModal,
    setTabChangeSaveConfirm,
    setTabChangeInvalidConfirm,
    setRentAdjustmentModal,
    setPaymentProcessModalOpen,
    setShowRiskRatingModal,
    setShowMaxRentModal,
    setMaxRentHistoryModalKey,
    setPolicyDeclarationModalOpen,
    setReimbursementExplanationOpen,
    setValidationModal,
    setSharedPhoneErrorOpen,
    setAddressErrorOpen,
    setImportPreviousAddressOpen,
    setLeaseDetailsChangedConfirmOpen,
    setLeaseStartDateAdjustConfirmOpen,
    setReimbursementApplicationDetailsConfirmOpen,
    setNoEmploymentModalOpen,
    setAboutYourselfExposeInlineValidation,
    setPropertyExposeInlineValidation,
    setIncomeExposeInlineValidation,
    setIsEdit,
    setFormData,
    setDirtyTabs,
    setUpdateTriggerCounter,
    pendingTabAfterSaveRef,
    saveModeRef,
    leaseChangeActionRef,
    submitWithoutLeaseDateUpdateRef,
    noEmploymentConfirmedRef,
    handleTabChange,
    handleCoResidentClick,
    performImpersonate,
    handleNinjaEquifaxScoreToolbarClick,
    handleRiskRatingAdjustClick,
    handleDownloadTerms,
    refreshApplicationDetail,
    handleFormChange,
    handleSave,
    handleCancelEdit,
    importPreviousAddresses,
    setImportPreviousDismissed,
    importPreviousEmployment,
    dismissImportPreviousEmployment,
  };
}
